import {Alert, Badge, Button, Col, Row, Spinner, Table} from "react-bootstrap";
import {
    dateToString,
    dateWithMaxTime, dateWithZeroTime, minutesToHoursAsString,
    timeDiffInMinutes,
    timeDiffToString,
    timeToString,
    getDayOfWeek
} from "../../tools";
import React, {useMemo} from "react";
import {EMPTY_LOGISTIC, RRLogisticType} from "./RRLogisticType";
import {AddIconButton, PrintIconButton} from "../../components/IconButtons";
import {RREmployeeType} from "../../types/RREmployeeType";
import LogisticDetails from "./LogisticDetails";
import {useCollectionData} from "react-firebase-hooks/firestore";
import {collection, orderBy, query, where} from "firebase/firestore";
import {appFirestore} from "../../../index";
import {trucksFBConverter} from "../trucks/TrucksFBConverter";
import {logisticsFBConverter} from "./LogisticsFBConverter";
import LogisticReportDetails from "./ReportDetails";
import RoadBookPreview from "../../components/RoadBookPreview";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faInfoCircle} from "@fortawesome/free-solid-svg-icons";
import BankHolidayToggle from "../../components/BankHolidayToggle";
import CenteredColumn from "../../components/CenteredColumn";
import {transportErrors} from "./transportValidation";
import {useReactToPrint} from "react-to-print";

interface LogisticsCalendarProps {
    firstDate: Date;
    daysCount: number;
    drivers: RREmployeeType[];
}

function RenderLogisticItem({logistic, onClick, onEditReport, className}:
                            {
                                logistic: RRLogisticType,
                                onClick?: () => void,
                                onEditReport: () => void,
                                className?: string
                            }) {
    return (
        <Col
            xs={"12"}
            className={(className ? className + ' ' : '') + "mb-1 cursor-pointer border rounded p-2"}>
            <Row className={"gap-1"}>
                <Col onClick={onClick}>
                    {
                        logistic.dayType === "WORKDAY" ? (
                                <div>
                                    <Badge bg={"dark"}>
                                        {timeToString(logistic.startDate)}
                                    </Badge><span className={"fw-bold"}>-</span>
                                    <Badge bg={"dark"}>
                                        {timeToString(logistic.endDate)}
                                    </Badge>
                                    {
                                        logistic.workInStore && (
                                            <Badge bg={"info"} className={"ms-2 text-dark"}>
                                                СКЛАД
                                            </Badge>
                                        )
                                    }
                                    {
                                        !logistic.workInStore && <>
                                            <span className={"fw-bold ms-3 me-2"}>{logistic.eventNumber}</span>
                                            <div
                                                className={"text-secondary mt-2"}> {logistic.truck.name} ({logistic.truck.plate})
                                            </div>
                                        </>
                                    }
                                </div>) :
                            (
                                <div>
                                    <Badge bg={"dark"}>
                                        {
                                            logistic.dayType === "HOLIDAY" ? "ОТПУСК" :
                                                logistic.dayType === "SICK" ? "БОЛНИЧЕН" :
                                                    logistic.dayType === "DAYOFF" ? "ОТСЪСТВИЕ" : "ДРУГО"
                                        }
                                    </Badge>
                                </div>
                            )
                    }
                </Col>
                {
                    logistic.planNotes && (
                        <Col xs={"auto"} className={"m-0 p-0"}>
                            <div className={"text-light bg-info"}
                                 style={{width: "22px", height: "22px", textAlign: "center", lineHeight: "22px"}}>
                                <span title={logistic.planNotes}>
                                    <FontAwesomeIcon icon={faInfoCircle}/>
                                </span>
                            </div>
                        </Col>
                    )
                }
                {
                    logistic.dayType === "WORKDAY" && (
                        <Col xs={"auto"}>
                            {
                                logistic.reportStartDate && logistic.reportEndDate ? (<>
                                        <div className={"text-secondary"} onClick={() => onEditReport()}>
                                            <Badge bg={"dark"}>
                                                {timeToString(logistic.reportStartDate)}
                                            </Badge><span className={"fw-bold"}>-</span>
                                            <Badge bg={"dark"}>
                                                {timeToString(logistic.reportEndDate)}
                                            </Badge>
                                        </div>
                                        <div
                                            className={"text-secondary mt-2"}> {
                                            timeDiffToString(logistic.reportStartDate, logistic.reportEndDate, 0)
                                        }</div>
                                    </>
                                ) : (
                                    <Button
                                        size={"sm"}
                                        variant={"outline-secondary"}
                                        onClick={() => onEditReport()}
                                        hidden={logistic.workInStore}
                                    >
                                        ...
                                    </Button>
                                )
                            }
                        </Col>
                    )
                }
            </Row>
        </Col>
    )
        ;
}

export default function LogisticsCalendar({firstDate, daysCount, drivers}: LogisticsCalendarProps) {

    // state for Road Book (Пътна книжка) modal
    const [roadBookModal, setRoadBookModal] = React.useState<{show: boolean, employee?: RREmployeeType, date?: Date, logistics?: RRLogisticType[]}>({show: false});

    const trucksCollection = useMemo(
        () => collection(appFirestore, "trucks").withConverter(trucksFBConverter),
        []
    );
    const [trucks] = useCollectionData(trucksCollection);

    const lastDate = useMemo(
        () => {
            const date = new Date(firstDate);
            date.setDate(date.getDate() + daysCount - 1);
            return dateWithMaxTime(date);
        }, [firstDate, daysCount]
    )
    const logisticsQuery = useMemo(() => query(
        collection(appFirestore, "logistics"),
        where("startDate", ">=", firstDate),
        where("startDate", "<", lastDate),
        orderBy("startDate", "asc")
    ).withConverter(logisticsFBConverter), [firstDate, lastDate]);
    const [logistics, loadingLogistics, errorLogistics] = useCollectionData(logisticsQuery);

    const [editLogistic, setEditLogistic] = React.useState<RRLogisticType | null>(null);
    const [editingReport, setEditingReport] = React.useState<RRLogisticType | null>(null);

    function validateLogistic(logistic: RRLogisticType): string[] {
        const errors = transportErrors(logistic, "plan");
        if (logistic.startDate < firstDate) {
            errors.push("Началната дата не може да е преди " + dateToString(firstDate));
        }

        // The callable validates conflicts against all current documents. The
        // client only performs deterministic form validation, so a partial
        // calendar range can never incorrectly reject a valid save.
        return errors;
    }

    // Calculate total hours for a driver on a specific date (used in rendering)
    function calculateDriverHours(driverId: string, date: Date) {
        let total = 0;
        const dateStart = dateWithZeroTime(date)!;
        const dateEnd = dateWithMaxTime(date)!;
        if (!logistics) {
            return total;
        }
        logistics
            .filter((logistic: RRLogisticType) => logistic.driverId === driverId && (
                logistic.startDate >= dateStart &&
                logistic.startDate <= dateEnd
            ))
            .forEach((logistic: RRLogisticType) => {
                const hasReportedPeriod = logistic.reportStartDate && logistic.reportEndDate;
                const startDate = hasReportedPeriod ? logistic.reportStartDate : logistic.startDate;
                const endDate = hasReportedPeriod ? logistic.reportEndDate : logistic.endDate;

                if (startDate && endDate) {
                    total += timeDiffInMinutes(startDate, endDate, 0)
                }
            });
        return minutesToHoursAsString(total);
    }

    // Sort drivers by their listIndex_logistics field so the calendar displays them in the requested order
    const sortedDrivers = useMemo(() => {
        if (!drivers) return [] as RREmployeeType[];
        return [...drivers].sort((a: RREmployeeType, b: RREmployeeType) =>
            (Number(a.listIndex_logistics) || 0) - (Number(b.listIndex_logistics) || 0)
        );
    }, [drivers]);

    // Prepare print handler early (hook must be called on every render) — targets off-screen printable root
    const handlePrintRoadBook = useReactToPrint({
        content: () => document.getElementById('roadbook-print-root') as HTMLElement | null,
        documentTitle: 'Пътен лист'
    });

    // show loading / error states early
    if (loadingLogistics) {
        return <Row>
            <Col>
                <Spinner animation={"border"}/>
            </Col>
        </Row>
    }

    if (errorLogistics) {
        return <Row>
            <Col>
                <Alert variant={"danger"}>
                    Грешка при зареждане на логистиката: {errorLogistics.message}
                </Alert>
            </Col>
        </Row>;
    }

    function addNewLogistic(employee: RREmployeeType, forDate: Date) {
        if (!employee.id) {
            return;
        }

        setEditLogistic({
            ...EMPTY_LOGISTIC,
            driverId: employee.id,
            driverName: employee.name,
            startDate: forDate,
            endDate: forDate
        })
    }

    function openRoadBookPreview(employee: RREmployeeType, forDate: Date, logisticsForDay?: RRLogisticType[]) {
        setRoadBookModal({show: true, employee, date: forDate, logistics: logisticsForDay});
    }

    function closeRoadBookPreview() {
        setRoadBookModal({show: false});
    }

    // helper to map a logistic dayType to bootstrap bg + optional text color classes
    function getLogisticBgClass(dayType: string) {
        switch(dayType) {
            case "HOLIDAY":
                return "bg-secondary text-light";
            case "SICK":
                return "bg-dark text-light";
            case "DAYOFF":
                return "bg-warning text-dark";
            default:
                return "bg-light";
        }
    }

    return (
        <div className={"d-block overflow-scroll"}>
            <Table striped bordered hover size={"sm"}>
                <thead style={{zIndex: 1000}}>
                {/* Single header row: Date + Employee + per-day cells with BankHolidayToggle */}
                <tr>
                    <th>Дата:</th>
                    <CenteredColumn header className={"w-240px td-left-dbl-border text-nowrap"} colSpan={1}
                                     style={{backgroundColor: 'antiquewhite'}}>
                        Служител
                    </CenteredColumn>
                    {
                        Array.from({length: daysCount}, (_, index) => {
                            const date = new Date(firstDate);
                            date.setDate(date.getDate() + index);
                            const dateStr = dateToString(date);
                            return (
                                <CenteredColumn header
                                                  key={index}
                                                  colSpan={1}
                                                  className={"td-left-dbl-border text-nowrap"}
                                                  style={{minWidth: "220px", backgroundColor: 'antiquewhite'}}>
                                    <span className={"cursor-pointer"}>
                                        {dateStr} ({getDayOfWeek(date).substring(0, 3)})
                                    </span>
                                    <br/><br/>&nbsp;
                                    <BankHolidayToggle departmentKey={"logistics"} dateStr={dateStr} />
                                </CenteredColumn>
                            );
                        })
                    }
                </tr>
                </thead>
                <tbody>
                {
                    // use sortedDrivers so rows appear ordered by listIndex_logistics
                    sortedDrivers.map((employee: RREmployeeType) => {
                        return (
                            <tr key={employee.id} className={"bg-light"}>
                                <td>
                                </td>
                                <td>
                                    <div className={"mt-2"}>{employee.name}</div>
                                </td>
                                {
                                    Array.from({length: daysCount}, (_, index) => {
                                        const date = new Date(firstDate);
                                        date.setDate(date.getDate() + index);
                                        const totalHours = calculateDriverHours(employee.id!, date);
                                        // show all logistics for the employee/day (do NOT filter by truck here)
                                        const employeeLogistics = logistics?.filter(logistic =>
                                            logistic.driverId === employee.id &&
                                            logistic.startDate >= dateWithZeroTime(date)! &&
                                            logistic.startDate <= dateWithMaxTime(date)!
                                        );

                                        // separate list that contains only logistics with a truck — used when opening/creating RoadBook
                                        const employeeLogisticsWithTruck = employeeLogistics?.filter(logistic =>
                                            logistic.truck && logistic.truck.plate && logistic.truck.plate.toString().trim() !== ''
                                        );

                                        return (
                                            <td key={index}>
                                                <Row className={"ms-1 gap-1"} style={{maxWidth: "210px"}}>
                                                    {
                                                        employeeLogistics && employeeLogistics
                                                            .map((logistic: RRLogisticType, idx: number) => {
                                                                const itemClass = (idx === 0 ? "td-left-dbl-border " : "") + getLogisticBgClass(logistic.dayType);
                                                                return <RenderLogisticItem
                                                                    logistic={logistic}
                                                                    key={logistic.id}
                                                                    className={itemClass}
                                                                    onClick={() => setEditLogistic(logistic)}
                                                                    onEditReport={() => {
                                                                        setEditLogistic(null);
                                                                        setEditingReport(logistic);
                                                                    }}
                                                                />
                                                            })
                                                    }

                                                    {
                                                        employeeLogistics &&
                                                        ((employeeLogistics.length > 0 &&
                                                                employeeLogistics[0].dayType === "WORKDAY") ||
                                                            employeeLogistics.length === 0) &&
                                                        (<Col xs={"auto"}
                                                              className={"mb-1 cursor-pointer border rounded bg-warning p-2"}>
                                                            <div className={"text-dark w-120px float-start"}>
                                                                <div className={"float-start"}>Всичко: <strong>{totalHours}</strong>
                                                                </div>
                                                                <div className={"float-end"}>
                                                                    <AddIconButton
                                                                        small
                                                                        onClick={() => addNewLogistic(employee, date)}/>
                                                                    {
                                                                        // Show print button only when there are hours AND there are logistics with a truck
                                                                        totalHours && totalHours !== "0ч" && employeeLogisticsWithTruck && employeeLogisticsWithTruck.length > 0 && (
                                                                            <PrintIconButton small onClick={() => openRoadBookPreview(employee, date, employeeLogisticsWithTruck)} className={"ms-1"}/>
                                                                        )
                                                                    }
                                                                </div>
                                                            </div>
                                                        </Col>)
                                                    }
                                                </Row>
                                            </td>
                                        );
                                    })
                                }
                            </tr>
                        );
                    })
                }
                </tbody>
            </Table>

            {
                editLogistic && (
                    <LogisticDetails
                        logistic={editLogistic}
                        trucks={trucks ?? []}
                        drivers={drivers}
                        logistics={logistics ?? []}
                        calendarPeriodFrom={firstDate}
                        calendarPeriodTo={lastDate ?? firstDate}
                        validateLogistic={validateLogistic}
                        onDone={() => setEditLogistic(null)}
                    />
                )
            }

            {
                editingReport && (
                    <LogisticReportDetails
                        logistic={editingReport}
                        onDone={
                            () => {
                                setEditingReport(null);
                                setEditLogistic(null);
                            }
                        }
                    />
                )
            }

            {/* Road book (Пътна книжка) preview component */}
            <RoadBookPreview
                show={roadBookModal.show}
                employee={roadBookModal.employee}
                date={roadBookModal.date}
                logistics={roadBookModal.logistics}
                onClose={closeRoadBookPreview}
                onPrint={handlePrintRoadBook}
            />
        </div>
    );
}
